/**
 * A project's periods (design 3.7): releases and sprints, phases and iterations, by the names its
 * workflow gives them. Fixed periods are added by hand; cadences keep making them for projects with
 * no end. A template holds them as days after the project's start rather than dates.
 */
import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Cadence, Container, Timebox, type TimeboxLength, type TimeboxLevel } from "@workcore/schemas";
import { api, ApiError, get } from "../../api";
import { Breadcrumbs, EmptyState, ErrorBox, PageHeader, Panel, RowMenu, StatusBadge, toast } from "../../ui";
import { confirmAction, confirmImpact } from "../../confirm";
import { InlineText, useDefinition } from "../editor/shared";
import { LengthInput, describeLength } from "../editor/Timeboxes";
import { useMe } from "./Projects";

const key = (slug: string, id: number, what: string) => ["ws", slug, "container", id, what];

interface WriteReq {
  method: string;
  path: string;
  body?: unknown;
  query?: Record<string, string>;
  impact?: string;
}

export function PeriodsPage() {
  const { slug = "", id: idParam = "" } = useParams();
  const id = Number(idParam);
  const q = useQueryClient();
  const me = useMe(slug).data;
  const container = useQuery({ queryKey: key(slug, id, "detail"), queryFn: async () => Container.parse(await get(`/v1/ws/${slug}/containers/${id}`)) });
  const periods = useQuery({ queryKey: key(slug, id, "timeboxes"), queryFn: async () => z.array(Timebox).parse(await get(`/v1/ws/${slug}/containers/${id}/timeboxes`)) });
  const cadences = useQuery({ queryKey: key(slug, id, "cadences"), queryFn: async () => z.array(Cadence).parse(await get(`/v1/ws/${slug}/containers/${id}/cadences`)) });
  const c = container.data;
  const def = useDefinition(slug, c?.definitionId ?? 0).data;
  const refresh = () => q.invalidateQueries({ queryKey: ["ws", slug, "container", id] });
  // every change: dry run first, then ask if it touches data, then commit (design 16.5)
  const write = useMutation({
    mutationFn: async (w: WriteReq) => {
      const opts = { ...(w.body !== undefined ? { body: w.body } : {}), ...(w.query ? { query: w.query } : {}) };
      await api(w.method, w.path, { ...opts, dryRun: true });
      if (w.impact && !(await confirmImpact(w.impact))) return false;
      await api(w.method, w.path, opts);
      return true;
    },
    onSuccess: (done) => done && toast("Saved"),
    onSettled: refresh,
  });
  if (!c || !def) return <ErrorBox error={container.error} />;

  const tpl = c.isTemplate;
  const canEdit = !!me && (me.isSystemAdmin || (tpl ? me.roles.some((r) => r.role === "workflow_admin" && r.scopeId === c.definitionId) : me.roles.some((r) => r.role === "project_admin" && r.scopeId === id)));
  const levels = [...def.timeboxLevels].sort((a, b) => a.sort - b.sort);
  const all = periods.data ?? [];
  const levelOf = (lid: number) => levels.find((l) => l.id === lid);

  return (
    <>
      <PageHeader
        back={<Breadcrumbs items={[{ label: "Projects", to: `/w/${slug}/projects` }, { label: c.name }]} />}
        title={`${c.name}: periods`}
        description={tpl ? "A template's periods are days after the project's start; each project made from it gets them as dates." : "When the work is meant to happen. Periods with dates open and close by the calendar unless you set their status by hand."}
      />
      <ErrorBox error={periods.error ?? write.error} />
      {levels.length === 0 ? (
        <EmptyState title="This workflow has no timeboxes" body={`Add levels such as Release and Sprint in the '${def.name}' workflow's Timeboxes tab, then plan this project's periods here.`} />
      ) : (
        <>
          {!tpl && <ProjectDates slug={slug} c={c} canEdit={canEdit} onDone={refresh} />}
          <Panel title="Periods">
            {all.length === 0 ? (
              <p className="muted">No periods yet. Add them one by one below, or start a cadence to have them made as time goes on.</p>
            ) : (
              <table className="grid">
                <thead><tr><th>Period</th><th>{tpl ? "From the start" : "Dates"}</th><th>Status</th><th className="opt">Made by</th><th /></tr></thead>
                <tbody>
                  {ordered(all, levels).map(({ t, depth }) => (
                    <PeriodRow key={t.id} slug={slug} t={t} depth={depth} level={levelOf(t.levelId)} tpl={tpl} canEdit={canEdit} write={write.mutate} />
                  ))}
                </tbody>
              </table>
            )}
            {canEdit && <AddPeriod slug={slug} id={id} levels={levels} periods={all} tpl={tpl} write={write.mutate} />}
          </Panel>
          <Cadences slug={slug} id={id} levels={levels} cadences={cadences.data ?? []} tpl={tpl} canEdit={canEdit} write={write.mutate} />
        </>
      )}
    </>
  );
}

/** Top-level periods in order, each followed by its children (and theirs). */
function ordered(all: Timebox[], levels: TimeboxLevel[]): { t: Timebox; depth: number }[] {
  const out: { t: Timebox; depth: number }[] = [];
  const sortKey = (t: Timebox) => `${t.startDate ?? ""}${String(t.offsetDays ?? 0).padStart(6, "0")}${String(t.sequence).padStart(6, "0")}`;
  const walk = (parent: number | null, depth: number) => {
    for (const t of all.filter((x) => x.parentId === parent).sort((a, b) => sortKey(a).localeCompare(sortKey(b)))) {
      out.push({ t, depth });
      walk(t.id, depth + 1);
    }
  };
  walk(null, 0);
  // periods whose parent is not listed (e.g. deleted) still show, at their own level
  for (const t of all) if (!out.some((o) => o.t.id === t.id)) out.push({ t, depth: levels.findIndex((l) => l.id === t.levelId) });
  return out;
}

function PeriodRow({ slug, t, depth, level, tpl, canEdit, write }: { slug: string; t: Timebox; depth: number; level: TimeboxLevel | undefined; tpl: boolean; canEdit: boolean; write: (w: WriteReq) => void }) {
  const path = `/v1/ws/${slug}/timeboxes/${t.id}`;
  const patch = (body: unknown) => write({ method: "PATCH", path, body });
  const label = level?.label ?? "period";
  const close = (carryOver: boolean) =>
    void confirmAction({
      title: `Close '${t.name}'?`,
      body: carryOver ? `Work still to do or in progress moves to the next ${label.toLowerCase()}.` : "Unfinished work stays where it is.",
      action: "Close",
      check: () => api("POST", `${path}/close`, { body: { carryOver }, dryRun: true }),
    }).then((ok) => ok && write({ method: "POST", path: `${path}/close`, body: { carryOver } }));
  const remove = async () => {
    try {
      await api("DELETE", path, { dryRun: true });
      if (await confirmAction({ title: `Delete '${t.name}'?`, body: "It disappears from the project. Undo puts it back.", action: "Delete", danger: true })) write({ method: "DELETE", path });
    } catch (e) {
      // items are allocated to it: offer to clear them in the same change
      if (!(e instanceof ApiError) || e.code !== "conflict" || !e.message.includes("unallocate")) return write({ method: "DELETE", path }); // shows the refusal
      if (await confirmImpact(`${e.message.split(";")[0]}. Deleting '${t.name}' clears their allocation.`)) write({ method: "DELETE", path, query: { unallocate: "true" } });
    }
  };
  return (
    <tr className={t.status === "closed" ? "inactive" : ""}>
      <td style={{ paddingLeft: `${8 + depth * 20}px` }}>
        <span className="muted small">{label} </span>
        <InlineText label={`${t.name} name`} value={t.name} disabled={!canEdit} onSave={(v) => patch({ name: v })} />
      </td>
      <td>
        {tpl ? (
          canEdit ? (
            <span className="inline-field length">
              day <input type="number" min={0} aria-label={`${t.name} offset`} defaultValue={t.offsetDays ?? ""} onBlur={(e) => e.target.value !== String(t.offsetDays ?? "") && patch({ offsetDays: Number(e.target.value), lengthDays: t.lengthDays ?? 14 })} />
              for <input type="number" min={1} aria-label={`${t.name} length`} defaultValue={t.lengthDays ?? ""} onBlur={(e) => e.target.value !== String(t.lengthDays ?? "") && patch({ offsetDays: t.offsetDays ?? 0, lengthDays: Number(e.target.value) })} /> days
            </span>
          ) : t.offsetDays !== null ? `day ${t.offsetDays}, ${t.lengthDays} days` : "—"
        ) : canEdit ? (
          <span className="inline-field length">
            <input type="date" aria-label={`${t.name} start`} value={t.startDate ?? ""} onChange={(e) => patch({ startDate: e.target.value || null })} />
            <input type="date" aria-label={`${t.name} end`} value={t.endDate ?? ""} onChange={(e) => patch({ endDate: e.target.value || null })} />
          </span>
        ) : (
          `${t.startDate ?? "—"} to ${t.endDate ?? "—"}`
        )}
      </td>
      <td>
        {tpl ? (
          <span className="muted">—</span>
        ) : (
          <>
            <StatusBadge status={t.status} />
            {!t.autoStatus && <span className="muted small" title="Set by hand; the calendar no longer changes it"> (set by hand)</span>}
          </>
        )}
      </td>
      <td className="opt muted small">{t.cadenceId ? "a cadence" : "added here"}</td>
      <td className="row-actions">
        {canEdit && (
          <RowMenu
            label={`Actions for ${t.name}`}
            items={[
              !tpl && t.status === "planned" && { label: "Start now", onSelect: () => patch({ status: "active" }) },
              !tpl && t.status !== "closed" && { label: "Close…", onSelect: () => close(level?.carryOver ?? false) },
              !tpl && t.status !== "closed" && level?.carryOver && { label: "Close, leaving work here…", onSelect: () => close(false) },
              !tpl && t.status === "closed" && { label: "Reopen", onSelect: () => patch({ status: "active" }) },
              { label: "Delete…", danger: true, onSelect: () => void remove() },
            ]}
          />
        )}
      </td>
    </tr>
  );
}

function AddPeriod({ slug, id, levels, periods, tpl, write }: { slug: string; id: number; levels: TimeboxLevel[]; periods: Timebox[]; tpl: boolean; write: (w: WriteReq) => void }) {
  const [levelId, setLevelId] = useState(levels[0]!.id);
  const [parentId, setParentId] = useState("");
  const [name, setName] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [offset, setOffset] = useState("");
  const [length, setLength] = useState("");
  const level = levels.find((l) => l.id === levelId)!;
  const above = levels.find((l) => l.sort === level.sort - 1);
  const parents = above ? periods.filter((p) => p.levelId === above.id) : [];
  const add = (e: FormEvent) => {
    e.preventDefault();
    const dates = tpl ? { offsetDays: offset ? Number(offset) : null, lengthDays: length ? Number(length) : null } : { startDate: start || null, endDate: end || null };
    write({ method: "POST", path: `/v1/ws/${slug}/containers/${id}/timeboxes`, body: { levelId, parentId: parentId ? Number(parentId) : null, name, ...dates } });
    setName("");
    setOffset("");
    setLength("");
    setStart("");
    setEnd("");
  };
  return (
    <form autoComplete="off" className="inline wrap" onSubmit={add}>
      <label className="inline-field"><span>Level</span><select aria-label="New period level" value={levelId} onChange={(e) => (setLevelId(Number(e.target.value)), setParentId(""))}>
        {levels.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
      </select></label>
      {above && (
        <label className="inline-field"><span>Inside</span><select aria-label="New period inside" required value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="">choose a {above.label.toLowerCase()}…</option>
          {parents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select></label>
      )}
      <label className="inline-field"><span>Name</span><input aria-label="New period name" required placeholder={`${level.label} 1`} value={name} onChange={(e) => setName(e.target.value)} /></label>
      {tpl ? (
        <>
          <label className="inline-field"><span>Starts on day</span><input aria-label="New period offset" type="number" min={0} value={offset} onChange={(e) => setOffset(e.target.value)} /></label>
          <label className="inline-field"><span>Length in days</span><input aria-label="New period length" type="number" min={1} value={length} onChange={(e) => setLength(e.target.value)} /></label>
        </>
      ) : (
        <>
          <label className="inline-field"><span>Start</span><input aria-label="New period start" type="date" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label className="inline-field"><span>End</span><input aria-label="New period end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </>
      )}
      <button type="submit">Add period</button>
    </form>
  );
}

function Cadences({ slug, id, levels, cadences, tpl, canEdit, write }: { slug: string; id: number; levels: TimeboxLevel[]; cadences: Cadence[]; tpl: boolean; canEdit: boolean; write: (w: WriteReq) => void }) {
  const [levelId, setLevelId] = useState(levels.at(-1)!.id);
  const level = levels.find((l) => l.id === levelId)!;
  const [length, setLength] = useState<TimeboxLength | null>(level.defaultLength);
  const [from, setFrom] = useState("");
  const [pattern, setPattern] = useState("");
  const [ahead, setAhead] = useState("3");
  const top = level.sort === 0;
  const start = (e: FormEvent) => {
    e.preventDefault();
    const anchor = from ? (tpl ? { anchorOffsetDays: Number(from) } : { anchorDate: from }) : {};
    write({ method: "POST", path: `/v1/ws/${slug}/containers/${id}/cadences`, body: { levelId, ...(length ? { length } : {}), ...anchor, ...(pattern ? { namePattern: pattern } : {}), horizon: Number(ahead) } });
  };
  return (
    <Panel title="Cadences">
      <p className="muted small">A cadence makes periods on a schedule and keeps the next few in place, for work with no end date. {tpl ? "In a template it makes nothing until a project is created from it." : "The calendar opens and closes them."}</p>
      {cadences.length > 0 && (
        <table className="grid">
          <thead><tr><th>Level</th><th>Every</th><th>{tpl ? "From day" : "From"}</th><th>Names</th><th>Ahead</th><th>Status</th><th /></tr></thead>
          <tbody>
            {cadences.map((cad) => (
              <tr key={cad.id} className={cad.active ? "" : "inactive"}>
                <td>{levels.find((l) => l.id === cad.levelId)?.label}</td>
                <td>{describeLength(cad.length)}</td>
                <td>{tpl ? (cad.anchorOffsetDays ?? "with its parent") : (cad.anchorDate ?? "with its parent")}</td>
                <td><code>{cad.namePattern}</code></td>
                <td>{cad.horizon}</td>
                <td><StatusBadge status={cad.active ? "active" : "stopped"} /></td>
                <td className="row-actions">
                  {canEdit && cad.active && <RowMenu label={`Actions for the ${levels.find((l) => l.id === cad.levelId)?.label} cadence`} items={[{ label: "Stop…", danger: true, onSelect: () => write({ method: "PATCH", path: `/v1/ws/${slug}/cadences/${cad.id}`, body: { active: false }, impact: "Stop making periods. Those already made stay." }) }]} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canEdit && (
        <form autoComplete="off" className="inline wrap" onSubmit={start}>
          <label className="inline-field"><span>Level</span><select aria-label="Cadence level" value={levelId} onChange={(e) => { const l = levels.find((x) => x.id === Number(e.target.value))!; setLevelId(l.id); setLength(l.defaultLength); }}>
            {levels.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select></label>
          <label className="inline-field"><span>Every</span><LengthInput label="Cadence length" value={length} onChange={setLength} /></label>
          <label className="inline-field"><span>{tpl ? "First starts on day" : "First starts"}{top ? "" : " (optional)"}</span>
            {tpl ? <input aria-label="Cadence start" type="number" min={0} required={top} value={from} onChange={(e) => setFrom(e.target.value)} /> : <input aria-label="Cadence start" type="date" required={top} value={from} onChange={(e) => setFrom(e.target.value)} />}
          </label>
          <label className="inline-field"><span>Names</span><input aria-label="Cadence names" placeholder={`${level.label} {n}`} value={pattern} onChange={(e) => setPattern(e.target.value)} /></label>
          <label className="inline-field"><span>Keep ahead</span><input aria-label="Cadence ahead" type="number" min={1} max={52} value={ahead} onChange={(e) => setAhead(e.target.value)} /></label>
          <button type="submit">Start cadence</button>
        </form>
      )}
      <p className="muted small">Names can use {"{n}"} (a running number), {"{yyyy}"}, {"{q}"}, {"{mm}"}, {"{ww}"} (from the start date) and {"{parent}"}. Below the top level, periods fill each parent from its start, the last one cut short to end with it.</p>
    </Panel>
  );
}

function ProjectDates({ slug, c, canEdit, onDone }: { slug: string; c: Container; canEdit: boolean; onDone: () => void }) {
  const [start, setStart] = useState(c.plannedStart ?? "");
  const [end, setEnd] = useState(c.plannedEnd ?? "");
  const [shift, setShift] = useState(false);
  const [error, setError] = useState<unknown>();
  const moved = !!c.plannedStart && !!start && start !== c.plannedStart;
  const days = moved ? Math.round((Date.parse(start) - Date.parse(c.plannedStart!)) / 86_400_000) : 0;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    const body = { plannedStart: start || null, ...(end !== (c.plannedEnd ?? "") || !(shift && moved) ? { plannedEnd: end || null } : {}), ...(shift && moved ? { shiftDates: true } : {}) };
    try {
      await api("PATCH", `/v1/ws/${slug}/containers/${c.id}`, { body, dryRun: true });
      if (shift && moved && !(await confirmImpact(`Move every period, cadence and planned date in '${c.name}' ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ${days > 0 ? "later" : "earlier"}. Actual dates stay as they are.`))) return;
      await api("PATCH", `/v1/ws/${slug}/containers/${c.id}`, { body });
      toast("Saved");
      setShift(false);
      onDone();
    } catch (err) {
      setError(err);
    }
  };
  return (
    <Panel title="Project dates">
      <form autoComplete="off" className="inline wrap" onSubmit={(e) => void save(e)}>
        <label className="inline-field"><span>Planned start</span><input aria-label="Project planned start" type="date" disabled={!canEdit} value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label className="inline-field"><span>Planned end</span><input aria-label="Project planned end" type="date" disabled={!canEdit} value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        <label className="check">
          <input type="checkbox" disabled={!canEdit || !moved} checked={shift && moved} onChange={(e) => setShift(e.target.checked)} />
          Move all dates by the same amount{moved ? ` (${days > 0 ? "+" : ""}${days} days)` : ""}
        </label>
        {canEdit && <button type="submit">Save dates</button>}
      </form>
      <p className="muted small">{c.plannedStart ? "Without the box ticked, periods already dated stay where they are." : "Periods copied from a template without dates get them from the planned start."}</p>
      <ErrorBox error={error} />
    </Panel>
  );
}
